import React, { useState, useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { Upload, FileText, Download, BarChart3, Hash, Type, TrendingUp, Loader2, Filter, X, Tags } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default categories selected (the most meaningful for content analysis)
const DEFAULT_SELECTED_POS = ['名詞', '動詞', '形容詞', '形状詞', '形容動詞', '副詞'];

const JapaneseTextAnalyzer = () => {
  const [analysisData, setAnalysisData] = useState(null);
  const [wordCloudImage, setWordCloudImage] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isGeneratingCloud, setIsGeneratingCloud] = useState(false);
  const [fileName, setFileName] = useState('');
  const [useDefaultStopwords, setUseDefaultStopwords] = useState(false);
  const [customStopwordsFile, setCustomStopwordsFile] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState(new Set(DEFAULT_SELECTED_POS));

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setFileName(file.name);
    setIsAnalyzing(true);
    setAnalysisData(null);
    setWordCloudImage(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('use_default_stopwords', useDefaultStopwords);
      
      if (customStopwordsFile) {
        formData.append('stopwords_file', customStopwordsFile);
      }

      const response = await axios.post(`${API}/analyze-text`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setAnalysisData(response.data);
      // Reset selection: select all categories that appear by default + any that came from backend
      if (response.data.available_categories) {
        const allCategories = response.data.available_categories.map((c) => c.pos);
        // Pre-select the meaningful categories that are present
        const preselected = allCategories.filter((pos) => DEFAULT_SELECTED_POS.includes(pos));
        setSelectedCategories(new Set(preselected.length > 0 ? preselected : allCategories));
      }
      setWordCloudImage(null);
      toast.success('Análisis completado exitosamente');
    } catch (error) {
      console.error('Error analyzing text:', error);
      toast.error(error.response?.data?.detail || 'Error al analizar el archivo');
    } finally {
      setIsAnalyzing(false);
    }
  }, [useDefaultStopwords, customStopwordsFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/plain': ['.txt'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    multiple: false,
  });

  const handleCustomStopwordsUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.txt')) {
        toast.error('Solo se aceptan archivos .txt para stopwords');
        return;
      }
      setCustomStopwordsFile(file);
      toast.success(`Stopwords personalizadas: ${file.name}`);
    }
  };

  const removeCustomStopwords = () => {
    setCustomStopwordsFile(null);
    toast.info('Stopwords personalizadas eliminadas');
  };

  // Filtered list based on selected categories
  const filteredFrequencyList = useMemo(() => {
    if (!analysisData || !analysisData.frequency_list) return [];
    if (selectedCategories.size === 0) return [];
    return analysisData.frequency_list.filter((item) => selectedCategories.has(item.pos));
  }, [analysisData, selectedCategories]);

  // Stats based on filtered list
  const filteredStats = useMemo(() => {
    const totalWords = filteredFrequencyList.reduce((sum, item) => sum + item.count, 0);
    const uniqueWords = filteredFrequencyList.length;
    return { totalWords, uniqueWords };
  }, [filteredFrequencyList]);

  const toggleCategory = (pos) => {
    const next = new Set(selectedCategories);
    if (next.has(pos)) {
      next.delete(pos);
    } else {
      next.add(pos);
    }
    setSelectedCategories(next);
    setWordCloudImage(null);
  };

  const selectAllCategories = () => {
    if (!analysisData) return;
    setSelectedCategories(new Set(analysisData.available_categories.map((c) => c.pos)));
    setWordCloudImage(null);
  };

  const deselectAllCategories = () => {
    setSelectedCategories(new Set());
    setWordCloudImage(null);
  };

  const generateWordCloud = async () => {
    if (!filteredFrequencyList.length) {
      toast.error('No hay palabras para generar la nube. Selecciona al menos una categoría.');
      return;
    }

    setIsGeneratingCloud(true);
    try {
      const response = await axios.post(`${API}/generate-wordcloud`, {
        frequency_list: filteredFrequencyList,
        max_words: 100,
      });

      setWordCloudImage(response.data.image);
      toast.success('Nube de palabras generada');
    } catch (error) {
      console.error('Error generating word cloud:', error);
      toast.error('Error al generar la nube de palabras');
    } finally {
      setIsGeneratingCloud(false);
    }
  };

  const exportToExcel = async () => {
    if (!filteredFrequencyList.length) {
      toast.error('No hay palabras para exportar. Selecciona al menos una categoría.');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/export-excel`,
        {
          frequency_list: filteredFrequencyList,
        },
        {
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'frecuencia_palabras.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Archivo Excel descargado');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Error al exportar a Excel');
    }
  };

  const downloadWordCloudImage = () => {
    if (!wordCloudImage) return;

    const link = document.createElement('a');
    link.href = wordCloudImage;
    link.setAttribute('download', 'nube_de_palabras.png');
    document.body.appendChild(link);
    link.click();
    link.remove();

    toast.success('Imagen descargada');
  };

  return (
    <div className="min-h-screen gradient-bg">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8" style={{ color: '#7DD3C0' }} />
            <div>
              <h1 className="text-4xl tracking-tight font-medium text-[#0A0F11]">
                Analizador de textos en japonés
              </h1>
              <p className="text-base text-[#4A5568] mt-1">
                Analiza frecuencia de palabras y genera nubes de palabras
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Stopwords Configuration */}
        <Card className="mb-6 stopwords-section" data-testid="stopwords-config-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5" style={{ color: '#7DD3C0' }} />
              <CardTitle className="text-xl tracking-tight font-medium text-[#0A0F11]">
                Configuración de Stopwords
              </CardTitle>
            </div>
            <CardDescription>
              Filtra palabras comunes que no aportan significado al análisis
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Default stopwords toggle */}
            <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
              <div className="flex-1">
                <Label htmlFor="default-stopwords" className="text-sm font-medium cursor-pointer">
                  Usar stopwords predeterminadas en japonés
                </Label>
                <p className="text-xs text-[#4A5568] mt-1">
                  Filtra partículas y palabras funcionales comunes (の, に, は, を, etc.)
                </p>
              </div>
              <Switch
                id="default-stopwords"
                checked={useDefaultStopwords}
                onCheckedChange={setUseDefaultStopwords}
                data-testid="default-stopwords-switch"
              />
            </div>

            {/* Custom stopwords upload */}
            <div className="p-3 bg-white rounded-lg border border-gray-200">
              <Label className="text-sm font-medium mb-2 block">
                Stopwords personalizadas (opcional)
              </Label>
              <p className="text-xs text-[#4A5568] mb-3">
                Sube un archivo .txt con tus propias palabras a filtrar (una por línea o separadas por comas)
              </p>
              
              {!customStopwordsFile ? (
                <label
                  htmlFor="custom-stopwords-input"
                  data-testid="custom-stopwords-upload-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 btn-secondary rounded-md cursor-pointer text-sm font-medium"
                >
                  <Upload className="w-4 h-4" />
                  Subir archivo de stopwords
                  <input
                    id="custom-stopwords-input"
                    type="file"
                    accept=".txt"
                    onChange={handleCustomStopwordsUpload}
                    className="hidden"
                  />
                </label>
              ) : (
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded border border-gray-200">
                  <FileText className="w-4 h-4 text-[#FF6B35]" />
                  <span className="text-sm flex-1" data-testid="custom-stopwords-filename">
                    {customStopwordsFile.name}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={removeCustomStopwords}
                    data-testid="remove-custom-stopwords-btn"
                    className="h-8 w-8 p-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        {/* Upload Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl tracking-tight font-medium text-[#0A0F11]">
              Subir archivo
            </CardTitle>
            <CardDescription>
              Sube un archivo .txt o .docx con texto en japonés para analizar
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              {...getRootProps()}
              data-testid="file-upload-zone"
              className={`upload-zone rounded-lg p-12 text-center cursor-pointer ${
                isDragActive ? 'drag-active' : ''
              }`}
            >
              <input {...getInputProps()} data-testid="file-input" />
              <div className="flex flex-col items-center gap-4">
                {isAnalyzing ? (
                  <Loader2 className="w-12 h-12 animate-spin" style={{ color: '#3E8E85' }} />
                ) : (
                  <Upload className="w-12 h-12" style={{ color: '#3E8E85' }} />
                )}
                <div>
                  <p className="text-lg font-medium text-[#0A0F11] mb-2">
                    {isDragActive
                      ? 'Suelta el archivo aquí'
                      : isAnalyzing
                      ? 'Analizando...'
                      : 'Arrastra un archivo o haz clic para seleccionar'}
                  </p>
                  <p className="text-sm text-[#4A5568]">
                    Formatos soportados: .txt, .docx
                  </p>
                  {fileName && (
                    <p className="text-sm font-medium mt-2" style={{ color: '#3E8E85' }}>
                      {fileName}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {analysisData && (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-8">
              <Card className="kpi-card" data-testid="total-words-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs tracking-[0.1em] uppercase text-[#4A5568] font-semibold mb-2">
                        Total de Palabras
                      </p>
                      <p className="text-5xl font-light tracking-tighter text-[#0A0F11]">
                        {filteredStats.totalWords.toLocaleString()}
                      </p>
                    </div>
                    <Type className="w-10 h-10 text-[#4A5568]" />
                  </div>
                </CardContent>
              </Card>

              <Card className="kpi-card" data-testid="unique-words-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs tracking-[0.1em] uppercase text-[#4A5568] font-semibold mb-2">
                        Palabras Únicas
                      </p>
                      <p className="text-5xl font-light tracking-tighter text-[#0A0F11]">
                        {filteredStats.uniqueWords.toLocaleString()}
                      </p>
                    </div>
                    <Hash className="w-10 h-10 text-[#4A5568]" />
                  </div>
                </CardContent>
              </Card>

              <Card className="kpi-card" data-testid="total-characters-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs tracking-[0.1em] uppercase text-[#4A5568] font-semibold mb-2">
                        Total de caracteres
                      </p>
                      <p className="text-5xl font-light tracking-tighter text-[#0A0F11]">
                        {analysisData.total_characters.toLocaleString()}
                      </p>
                    </div>
                    <BarChart3 className="w-10 h-10 text-[#4A5568]" />
                  </div>
                </CardContent>
              </Card>

              {analysisData.stopwords_used > 0 && (
                <Card className="kpi-card" data-testid="stopwords-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs tracking-[0.1em] uppercase text-[#4A5568] font-semibold mb-2">
                          Stopwords activas
                        </p>
                        <p className="text-5xl font-light tracking-tighter text-[#0A0F11]">
                          {analysisData.stopwords_used.toLocaleString()}
                        </p>
                      </div>
                      <Filter className="w-10 h-10" style={{ color: '#7DD3C0' }} />
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Categories Filter */}
            {analysisData.available_categories && analysisData.available_categories.length > 0 && (
              <Card className="mb-6" data-testid="categories-filter-card">
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <Tags className="w-5 h-5" style={{ color: '#7DD3C0' }} />
                      <CardTitle className="text-xl tracking-tight font-medium text-[#0A0F11]">
                        Categorías gramaticales
                      </CardTitle>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={selectAllCategories}
                        data-testid="select-all-categories-btn"
                      >
                        Todas
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={deselectAllCategories}
                        data-testid="deselect-all-categories-btn"
                      >
                        Ninguna
                      </Button>
                    </div>
                  </div>
                  <CardDescription>
                    Selecciona qué categorías incluir en la tabla, Excel y nube de palabras
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {analysisData.available_categories.map((cat) => (
                      <label
                        key={cat.pos}
                        htmlFor={`cat-${cat.pos}`}
                        data-testid={`category-checkbox-${cat.pos}`}
                        className="flex items-center gap-2 p-2 rounded-md border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <Checkbox
                          id={`cat-${cat.pos}`}
                          checked={selectedCategories.has(cat.pos)}
                          onCheckedChange={() => toggleCategory(cat.pos)}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-[#0A0F11] truncate">
                            {cat.pos_es}
                          </div>
                          <div className="text-xs text-[#4A5568]">
                            {cat.count} {parseInt(cat.count) === 1 ? 'palabra' : 'palabras'}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tabs for Frequency List and Word Cloud */}
            <Tabs defaultValue="frequency" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="frequency" data-testid="frequency-tab">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Lista de Frecuencia
                </TabsTrigger>
                <TabsTrigger value="wordcloud" data-testid="wordcloud-tab">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Nube de Palabras
                </TabsTrigger>
              </TabsList>

              <TabsContent value="frequency">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-2xl tracking-tight font-medium text-[#0A0F11]">
                          Frecuencia de Palabras
                        </CardTitle>
                        <CardDescription>
                          Palabras ordenadas por frecuencia de aparición
                        </CardDescription>
                      </div>
                      <Button
                        onClick={exportToExcel}
                        data-testid="export-excel-btn"
                        className="btn-primary"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Exportar a Excel
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="border rounded-lg overflow-hidden">
                      <div className="max-h-[600px] overflow-y-auto scroll-fade">
                        <Table>
                          <TableHeader className="table-header">
                            <TableRow>
                              <TableHead className="w-[80px]">#</TableHead>
                              <TableHead>Palabra</TableHead>
                              <TableHead className="w-[160px]">Categoría</TableHead>
                              <TableHead className="text-right w-[120px]">Frecuencia</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredFrequencyList.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={4} className="text-center py-8 text-[#4A5568]">
                                  No hay palabras que coincidan con las categorías seleccionadas
                                </TableCell>
                              </TableRow>
                            ) : (
                              filteredFrequencyList.map((item, index) => (
                                <TableRow
                                  key={`${item.word}-${item.pos}-${index}`}
                                  data-testid={`frequency-row-${index}`}
                                >
                                  <TableCell className="font-medium">{index + 1}</TableCell>
                                  <TableCell className="font-medium" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                                    {item.word}
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant="secondary" className="font-normal">
                                      {item.pos_es}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-right">{item.count}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="wordcloud">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-2xl tracking-tight font-medium text-[#0A0F11]">
                          Nube de Palabras
                        </CardTitle>
                        <CardDescription>
                          Visualización de las palabras más frecuentes
                        </CardDescription>
                      </div>
                      {!wordCloudImage ? (
                        <Button
                          onClick={generateWordCloud}
                          data-testid="generate-wordcloud-btn"
                          className="btn-primary"
                          disabled={isGeneratingCloud}
                        >
                          {isGeneratingCloud ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <BarChart3 className="w-4 h-4 mr-2" />
                          )}
                          Generar Nube de Palabras
                        </Button>
                      ) : (
                        <div className="flex gap-2">
                          <Button
                            onClick={generateWordCloud}
                            data-testid="regenerate-wordcloud-btn"
                            variant="outline"
                            disabled={isGeneratingCloud}
                          >
                            {isGeneratingCloud ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <BarChart3 className="w-4 h-4 mr-2" />
                            )}
                            Regenerar
                          </Button>
                          <Button
                            onClick={downloadWordCloudImage}
                            data-testid="download-wordcloud-btn"
                            className="btn-primary"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Descargar PNG
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="wordcloud-container rounded-lg bg-white p-6">
                      {wordCloudImage ? (
                        <img
                          src={wordCloudImage}
                          alt="Nube de palabras"
                          data-testid="wordcloud-image"
                          className="w-full h-auto rounded-lg"
                        />
                      ) : (
                        <div className="text-center py-12">
                          <BarChart3 className="w-16 h-16 mx-auto mb-4 text-[#4A5568]" />
                          <p className="text-[#4A5568]">
                            Haz clic en el botón para generar la nube de palabras
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}

        {/* Empty State */}
        {!analysisData && !isAnalyzing && (
          <Card className="text-center py-12">
            <CardContent>
              <FileText className="w-16 h-16 mx-auto mb-4" style={{ color: '#7DD3C0' }} />
              <h3 className="text-xl font-medium text-[#0A0F11] mb-2">
                Sube un archivo para comenzar
              </h3>
              <p className="text-[#4A5568]">
                Analiza textos en japonés y obtén estadísticas detalladas de frecuencia de palabras
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default JapaneseTextAnalyzer;
